import { DIAGNOSIS_SCHEMA_VERSION, rescoreVibrato } from './toDiagnosisRecord';
import {
  readItem as read,
  removeItem as remove,
  writeItem as write,
} from '../../../core';

import type { CaptureSource } from '../capture';
import type { DiagnosisRecord } from './toDiagnosisRecord';

/**
 * diagnosisStore — 진단 회차의 로컬 영속화(client-only, 서버 없음 — 플랜 Decision).
 *
 * **회차별 키 + 인덱스**로 나눈다(티켓 04). 단일 키에 배열로 두면 안 되는 이유:
 * 모바일 WebView는 `localStorage.setItem`을 몽키패치해 **값 전체를 postMessage로**
 * 네이티브에 미러링한다(`apps/mobile/.../injectedScripts.ts`). 이력을 한 키에 담으면
 * 회차를 하나 추가할 때마다 이력 전체가 RPC를 타 회차 수에 비례해 저장이 느려진다.
 * 회차별 키면 append 비용이 그 회차 크기로 고정된다. (IndexedDB는 이 미러가 안 걸려
 * **네이티브 영속화를 잃는다** — 별도 브리지 작업이 필요해 v1에선 안 쓴다.)
 *
 * localStorage 접근은 `core/localStore`가 방어적으로 감싼다(사생활 모드·용량 초과 대비).
 */

const INDEX_KEY = 'muzly.diagnosis.index';
const recordKey = (id: string): string => `muzly.diagnosis.${id}`;

/** 인덱스 상한 — 넘으면 오래된 회차부터 버린다(키까지 같이 지운다). */
export const MAX_HISTORY = 50;

/**
 * 저장·삭제 구독 — `useDiagnoses()`(`useSyncExternalStore`)가 "다시 확인하라"는 신호로 쓴다.
 *
 * `useState(loadDiagnoses)` 한 번만으로는 복원(비동기 저장)이 화면이 열린 뒤 끝나도 안
 * 보인다 — 저장·삭제가 날 때마다 알린다. **무엇이 바뀌었는지는 안 싣는다** — 캐시가
 * 유효한지는 `useDiagnoses`가 인덱스 내용으로 직접 확인한다(Seams: store는 알림만).
 */
type Listener = () => void;
const listeners = new Set<Listener>();

export const subscribeDiagnoses = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notifyDiagnoses = (): void => {
  listeners.forEach((listener) => listener());
};

/** 목록·정렬용 메타데이터만. 인덱스는 매번 통째로 오가므로 작게 유지한다. */
export interface DiagnosisIndexEntry {
  id: string;
  completedAt: number;
  source: CaptureSource;
}

/** 최신순. 깨진 인덱스는 빈 목록으로 떨어뜨린다(읽을 수 없으면 버린다 — v1 정책). */
export const listDiagnoses = (): DiagnosisIndexEntry[] => {
  const raw = read(INDEX_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as DiagnosisIndexEntry[];
    return Array.isArray(parsed)
      ? [...parsed].sort((a, b) => b.completedAt - a.completedAt)
      : [];
  } catch {
    return [];
  }
};

/** 모르는 스키마 버전은 버린다 — v1은 마이그레이션 대신 폐기다. */
export const loadDiagnosis = (id: string): DiagnosisRecord | null => {
  const raw = read(recordKey(id));
  if (!raw) {
    return null;
  }
  try {
    const record = JSON.parse(raw) as DiagnosisRecord;
    // 옛 산식의 비브라토 점수는 읽을 때 원값에서 다시 낸다(`rescoreVibrato`).
    return record.schemaVersion === DIAGNOSIS_SCHEMA_VERSION
      ? rescoreVibrato(record)
      : null;
  } catch {
    return null;
  }
};

/**
 * 최신순 전체 회차. 인덱스에 있으나 본체가 깨졌거나 스키마가 다른 회차는 조용히 빠진다
 * (읽을 수 없으면 버린다 — loadDiagnosis와 같은 정책).
 */
export const loadDiagnoses = (): DiagnosisRecord[] =>
  listDiagnoses()
    .map((entry) => loadDiagnosis(entry.id))
    .filter((record): record is DiagnosisRecord => record !== null);

/**
 * 저장하고 저장 여부를 낸다. **mock 세션은 쓰지 않는다** — 합성 프레임은 실측이 아니고,
 * 이력에 섞이면 나중에 어느 게 진짜였는지 구분할 방법이 없다. 걸러내는 자리를 여기 하나로
 * 두어 호출부마다 판단이 갈리지 않게 한다.
 *
 * `mic`(브라우저 마이크)은 **저장한다**. 추정기가 네이티브 MPM이 아니라 JS ACF라 수치를
 * 네이티브 회차와 나란히 비교하면 안 되지만, 실제 목소리를 실제로 잰 회차다 — 여기서
 * 버리면 브라우저에서 진단을 끝내도 홈·이력에 아무것도 안 남는다. 어느 경로였는지는
 * 레코드의 `source`가 그대로 들고 있으므로 나중에 갈라 볼 수 있다.
 */
export const saveDiagnosis = (record: DiagnosisRecord): boolean => {
  if (record.source === 'mock') {
    return false;
  }
  write(recordKey(record.id), JSON.stringify(record));

  const entry: DiagnosisIndexEntry = {
    id: record.id,
    completedAt: record.completedAt,
    source: record.source,
  };
  const next = [entry, ...listDiagnoses().filter((e) => e.id !== record.id)];
  next.slice(MAX_HISTORY).forEach((old) => remove(recordKey(old.id)));
  write(INDEX_KEY, JSON.stringify(next.slice(0, MAX_HISTORY)));
  notifyDiagnoses();
  return true;
};

export const clearDiagnoses = (): void => {
  listDiagnoses().forEach((e) => remove(recordKey(e.id)));
  remove(INDEX_KEY);
  notifyDiagnoses();
};

/**
 * 이 회차 **직전에** 끝난 회차. 이력 대비 비교의 기준선이다.
 *
 * 목록에서 자기 자신을 빼는 게 아니라 **더 오래된 것 중 첫 번째**를 고른다: 결과 화면이
 * 뜨기 전에 이번 회차가 이미 저장되므로(`useRecordMachine`) 자기 자신이 목록에 있고,
 * id로만 거르면 저장이 안 된 회차(mock)에서 오늘 것과 어제 것이 뒤바뀐다.
 *
 * mock 회차는 애초에 저장되지 않으니(`saveDiagnosis`) 기준선으로 **뽑히지** 않는다.
 * 반대 방향도 막는다 — mock 회차가 **비교하는 쪽**일 때도 기준선이 없다. 합성 음성과
 * 실측 회차를 견준 `이력 대비 137%`는 아무것도 재지 않은 숫자이고, 브라우저 데모
 * 스크린샷은 밖으로 돌아다닌다(`MockNotice`가 있는 이유와 같다).
 */
export const previousDiagnosis = (
  record: DiagnosisRecord,
): DiagnosisRecord | null => {
  if (record.source === 'mock') {
    return null;
  }
  // 목록만 훑고 **필요한 하나만** 읽는다 — 전부 파싱해서 하나를 고르면 50개 회차가 쌓인
  // 기기에서 결과 화면이 그만큼 느려진다. 시각은 목록이 들고 있어(저장할 때 레코드에서
  // 그대로 옮겨 적는다) 읽기 전에 고를 수 있다. 못 읽는 회차(옛 스키마)는 건너뛴다.
  for (const entry of listDiagnoses()) {
    if (entry.completedAt >= record.completedAt) {
      continue;
    }
    const previous = loadDiagnosis(entry.id);
    if (previous) {
      return previous;
    }
  }
  return null;
};
